import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Inbox, LogOut, Mail, Phone, Tag, CalendarDays, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = [
  { value: "new", label: "New", classes: "bg-[#F47721]/10 text-[#C85E12] border-[#F47721]/25" },
  { value: "contacted", label: "Contacted", classes: "bg-[#24589A]/10 text-[#24589A] border-[#24589A]/25" },
  { value: "quoted", label: "Quoted", classes: "bg-[#13263A]/10 text-[#13263A] border-[#13263A]/20" },
  { value: "closed", label: "Closed", classes: "bg-emerald-600/10 text-emerald-700 border-emerald-600/25" },
];

const statusMeta = (status) => STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

export default function AdminEnquiries() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/enquiries`, { withCredentials: true })
      .then((r) => setEnquiries(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load enquiries"));
  }, [user]);

  const statusCounts = useMemo(() => {
    const counts = { new: 0, contacted: 0, quoted: 0, closed: 0 };
    (enquiries || []).forEach((item) => {
      counts[item.status || "new"] += 1;
    });
    return counts;
  }, [enquiries]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    setError("");
    try {
      const response = await axios.patch(`${API}/enquiries/${id}/status`, { status }, { withCredentials: true });
      setEnquiries((items) => items.map((item) => (item.id === id ? response.data : item)));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update enquiry status");
    } finally {
      setUpdatingId("");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center" data-testid="admin-loading">
        <p className="text-[#59636D] text-sm uppercase tracking-[0.2em]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F4]" data-testid="admin-enquiries-page">
      <header className="border-b border-[#E5E8EA] bg-white/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <Logo markClass="h-9 w-auto" textSize="text-sm" textClassName="hidden sm:block" />
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="text-sm text-[#59636D] hover:text-[#17232D] transition-colors duration-300">
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="border border-[#17232D]/20 text-[#17232D] text-sm font-semibold py-2.5 px-5 hover:bg-[#17232D] hover:text-white transition-colors duration-300 flex items-center gap-2"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F47721]">Enquiry Management</p>
            <h1 className="font-manrope font-bold tracking-tight text-[#17232D] text-4xl mt-3">Website Enquiries</h1>
            <p className="text-[#59636D] text-sm mt-2" data-testid="enquiries-count">
              {enquiries ? `${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"} received` : "Loading…"}
            </p>
          </div>
          {enquiries && (
            <div className="flex flex-wrap gap-2" data-testid="enquiry-status-summary">
              {STATUS_OPTIONS.map((item) => (
                <span key={item.value} className={`rounded-full border px-3 py-1.5 font-inter text-[10px] font-semibold uppercase tracking-[0.14em] ${item.classes}`}>
                  {item.label}: {statusCounts[item.value]}
                </span>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p data-testid="enquiries-error" className="mt-6 text-sm text-red-700 border border-red-600/30 bg-red-600/10 px-4 py-3">
            {error}
          </p>
        )}

        {enquiries && enquiries.length === 0 && (
          <div data-testid="enquiries-empty" className="mt-16 border border-dashed border-[#17232D]/20 bg-white py-20 flex flex-col items-center gap-4 text-[#59636D]">
            <Inbox size={36} strokeWidth={1.5} />
            <p className="text-sm">No enquiries yet. New submissions from the contact form will appear here.</p>
          </div>
        )}

        <div className="mt-10 space-y-5" data-testid="enquiries-list">
          {enquiries?.map((q, i) => {
            const status = q.status || "new";
            const meta = statusMeta(status);
            return (
              <motion.article
                key={q.id}
                data-testid={`enquiry-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="border border-[#E5E8EA] bg-white shadow-sm rounded-md p-6 md:p-7"
              >
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-manrope font-bold tracking-tight text-[#17232D] text-xl">{q.name}</h2>
                      <span data-testid={`enquiry-status-${i}`} className={`rounded-full border px-3 py-1 font-inter text-[10px] font-semibold uppercase tracking-[0.14em] ${meta.classes}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-[#59636D]">
                      <span className="flex items-center gap-2"><Mail size={14} className="text-[#F47721]" />{q.email}</span>
                      {q.phone && <span className="flex items-center gap-2"><Phone size={14} className="text-[#F47721]" />{q.phone}</span>}
                      {q.service && <span className="flex items-center gap-2"><Tag size={14} className="text-[#F47721]" />{q.service}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row xl:flex-col gap-3 sm:items-center xl:items-end shrink-0">
                    <span className="flex items-center gap-2 text-xs text-[#59636D] uppercase tracking-[0.15em]">
                      <CalendarDays size={14} className="text-[#F47721]" />
                      {new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                    <label className="relative block">
                      <span className="sr-only">Update enquiry status</span>
                      <select
                        data-testid={`enquiry-status-select-${i}`}
                        value={status}
                        disabled={updatingId === q.id}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className="appearance-none rounded-md border border-[#E5E8EA] bg-[#F7F7F4] py-2.5 pl-4 pr-10 font-inter text-xs font-semibold uppercase tracking-[0.12em] text-[#17232D] outline-none transition-colors duration-300 focus:border-[#F47721] disabled:opacity-60"
                      >
                        {STATUS_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#F47721]" />
                    </label>
                  </div>
                </div>
                <p className="text-sm text-[#59636D] leading-relaxed mt-5 pt-5 border-t border-[#E5E8EA]">{q.message}</p>
              </motion.article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
